<template>
      <mdb-view class="overflow-auto view-gp-back" :style="bgStyle">

        <mdb-mask class="gp-hp-gradient">
          <div v-if="currentStep==1">
            <mdb-card class="text-center opacity-8">
              <mdb-card-header class="secondary-color">
                <h3>
                  <mdb-icon icon="user-check"/>
                  {{ $t("page.home.welcomeMessage", {name: userDetails.name}) }}
                </h3>
              </mdb-card-header>
              <mdb-card-body>
                <h5>{{ $t("page.newUser.fewMoreSteps") }}</h5>
                <div>{{ $t("page.newUser.knowingLocation") }}
                </div>
                <div class="text-info">
                  {{ $t("page.newUser.locationQuestion") }}
                </div>
                <div>
                  <mdb-btn @click="locationDetect" icon="map-pin">{{ $t("page.newUser.detectLocation") }}</mdb-btn>
                  <mdb-btn @click="locationEntry" icon="pencil">{{ $t("page.newUser.enterManually") }}</mdb-btn>
                </div>
              </mdb-card-body>
            </mdb-card>
          </div>
          <div v-if="currentStep==2">
            <mdb-card class="text-left opacity-8">
              <mdb-card-header class="secondary-color">
                <h3>
                  <mdb-icon icon="globe"/>
                  {{ $t("page.newUser.locationDetails") }}
                </h3>
              </mdb-card-header>
              <mdb-card-body>
                <div class="text-info">
                  {{ $t("page.newUser.locationInstructions") }}
                </div>
                <div>
                  <mdb-btn color="warning" icon="arrow-left" rounded size="sm" @click="currentStep=1">
                    {{ $t("common.button.back") }}
                  </mdb-btn>
                </div>
                <gp-select :dataObj="user" :possible-values="searchCountries"
                           :required="true" icon="globe"
                           :label="capitalize($tc('common.label.country',1))"
                           propertyName="country"/>

                <mdb-input v-model="user.city" fal icon="city"
                           :label="capitalize($tc('common.label.city',1))"
                           name="city"
                           type="text"/>
                <mdb-input v-model="user.postal" fal icon="mailbox"
                           :label="capitalize($t('common.label.postalCode'))"
                           name="postal"
                           type="text"/>
                <mdb-input v-model="user.timezone" disabled fal icon="clock"
                           :label="capitalize($t('common.label.timezone'))"
                           name="timezone"
                           type="text"
                           @click="showTimezoneSelector=true"/>
                <div>
                  <a role="button" @click="showTimezoneSelector=true">
                    <mdb-icon icon="list-alt"/>
                    {{ $t("page.newUser.changeTimezone") }}
                  </a>
                </div>
                <div>
                  <mdb-btn color="success" :disabled="!user.city" icon="save" rounded size="sm" @click="saveLocation">
                    {{ $t("common.button.save") }}
                  </mdb-btn>
                </div>
              </mdb-card-body>
            </mdb-card>
          </div>

          <div
            v-if="currentStep==3 && userDetails.groups.length>0 && ( (!userDetails.requireAvatar || (userDetails.requireAvatar && userDetails.hasAvatar)))">
            <mdb-card class="opacity-8">
              <mdb-card-header class="secondary-color">
                <h3>
                  <mdb-icon icon="check"/>
                  {{ capitalize($t("common.button.done")) }}
                </h3>
              </mdb-card-header>
              <mdb-card-body>
                <div>You're all set!</div>
                <div v-if="userDetails.visitedEvents && userDetails.visitedEvents.length > 0">
                  <div v-for="e of userDetails.visitedEvents" :key="e.id">
                    <router-link :to="'/event/show/' + e.id">Go to Event {{ e.title }}</router-link>
                  </div>
                </div>
                <div>
                  <mdb-btn @click="$router.push('/')" icon="home">Home</mdb-btn>
                </div>

              </mdb-card-body>
            </mdb-card>
          </div>

          <div v-if="currentStep==3 && userDetails.groups.length==0">
            <mdb-card class="opacity-8">
              <mdb-card-header class="secondary-color">
                <h3>
                  <mdb-icon icon="users"/>
                  {{ $t("page.newUser.joinOrCreateGroup") }}
                </h3>
              </mdb-card-header>
              <mdb-card-body>
                <h3>{{ $t("page.newUser.whatWouldYouLike") }}</h3>
                <div>
                  <mdb-btn @click="$router.push('/explore')" icon="search">{{ $t("page.newUser.findGroup") }}</mdb-btn>
                </div>
                <div>
                  <mdb-btn v-if="userDetails.mayCreateGroup" @click="$router.push('/newGroup')" icon="plus-circle">{{ $t("page.newUser.createGroup") }}
                  </mdb-btn>
                </div>
                <mdb-card v-if="userDetails.visited && userDetails.visited.length > 0" class="mb-4">
                  <mdb-card-header class="text-left skin-accent text-white">
                    <mdb-icon class="pr-1" icon="users"/>
                    {{ $t("page.newUser.visitedNotJoined") }}
                  </mdb-card-header>
                  <mdb-card-group deck>
                    <group-card v-for="group in userDetails.visited" v-bind:key="group.id" :group="group"/>
                  </mdb-card-group>

                </mdb-card>

              </mdb-card-body>
            </mdb-card>
          </div>

          <div
            v-if="currentStep==3 && userDetails.groups.length > 0 && userDetails.requireAvatar && !userDetails.hasAvatar">
            <mdb-card class="opacity-8">
              <mdb-card-header class="secondary-color">
                <h3>
                  <mdb-icon icon="users"/>
                  {{ $t("page.newUser.avatarRequired") }}
                </h3>
              </mdb-card-header>
              <mdb-card-body>
                <mdb-file-input v-on:getValue="uploadFile"/>
              </mdb-card-body>
            </mdb-card>

          </div>

          <mdb-modal v-if="showDetectedValues">
            <mdb-modal-header :close="false" class="secondary-color">
              <h3>
                <mdb-icon icon="map-pin"/>
                {{ $t("page.newUser.detectedLocation") }}
              </h3>
            </mdb-modal-header>
            <mdb-modal-body>
              <gp-select :dataObj="user" :possible-values="searchCountries"
                         :required="true" disabled icon="globe"
                         :label="capitalize($tc('common.label.country',1))"
                         propertyName="country"/>
              <mdb-input v-model="user.city" disabled fal icon="city"
                         :label="capitalize($tc('common.label.city',1))"
                         name="city"
                         type="text"/>
              <mdb-input v-model="user.postal" disabled fal icon="mailbox"
                         :label="capitalize($t('common.label.postalCode'))"
                         name="postal"
                         type="text"/>
              <mdb-input v-model="user.timezone" disabled fal icon="clock"
                         :label="capitalize($t('common.label.timezone'))"
                         name="timezone"
                         type="text"
                         @click="showTimezoneSelector=true"/>
              <mdb-btn icon="check" @click="confirmDetected">{{ capitalize($t("common.button.looksGood")) }}</mdb-btn>
              <mdb-btn icon="pencil" color="warning" @click="editDetected">{{ capitalize($t("common.button.edit")) }}
              </mdb-btn>
            </mdb-modal-body>
          </mdb-modal>

          <mdb-modal v-if="showTimezoneSelector" @close="showTimezoneSelector=false">
            <mdb-modal-header :close="true">
              Select Your Time Zone
            </mdb-modal-header>
            <mdb-modal-body>
              <timezone-select :clickHandler="timezoneSelected"></timezone-select>
            </mdb-modal-body>
          </mdb-modal>
        </mdb-mask>
        <div v-if="loggedIn && userDetails" class="gp-has-header mb-2 ml-4 mr-4 p-2">
          <mdb-card v-if="translationMode" class="mb-2">
            <mdb-card-header class="secondary-color">
              <h3>Translations</h3>
            </mdb-card-header>
            <mdb-card-body>
              <translate-section :items="translateItems" category="page" sub-category="newUser"/>
            </mdb-card-body>
          </mdb-card>
        </div>


      </mdb-view>


</template>

<script>
import {
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardGroup,
  mdbCardHeader,
  mdbCol,
  mdbFileInput,
  mdbIcon,
  mdbInput,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbRow,
  mdbSpinner,
  mdbStepper,
  mdbView
} from 'mdbvue';
import timezone from '../services/timezone'
import timezoneSelect from '../components/common/TimezoneSelect'
import gpSelect from '../components/common/GPSelect'
import iso from "iso-3166-1"
import gpCookie from '../services/gpCookie'
import axios from "axios";
import api from "../services/groupsPlaceApi";
import groupCard from './group/Card'
import translateSection from "./common/TranslateSection";

export default {
  name: 'New-User',
  components: {
    mdbInput,
    mdbBtn,
    mdbCard,
    mdbCardBody,
    mdbCardGroup,
    mdbIcon,
    mdbCardHeader,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbView,
    mdbMask,
    mdbSpinner,
    mdbStepper,
    mdbRow,
    mdbCol,
    mdbFileInput,
    gpSelect,
    timezoneSelect,
    groupCard,
    translateSection
  },
  computed: {
    apiKey: function () {
      return this.$store.state.apiKey
    },
    userDetails: function () {
      return this.$store.state.userDetails
    },
    loggedIn: function () {
      return this.$store.state.loggedIn
    },
    settings: function () {
      return this.$store.state.settings
    },
    translationMode: function () {
      return this.$store.state.translationMode
    },
    isDevelopment: function () {
      return process.env.NODE_ENV === 'development'
    },
    bgStyle: function () {
      if (!this.settings) return ""
      if (this.settings.loginBackground) return "background: url('" + this.settings.loginBackground + "'); background-repeat: no-repeat; background-size: cover; background-position: center top;"
      else return ""
    }
  },

  data() {
    return {
      countries: [],
      searchCountries: [],
      showTimezoneSelector: false,
      showDetectedValues: false,
      currentStep: 1,
      settingsStepper: [
        {icon: "user-check", far: true, name: "Welcome!"},
        {icon: "map-pin", name: "Location Details"},
        {icon: "check", name: "Finish"}
      ],
      options: {
        stepBtn: {color: "info", active: "amber", iconClass: "white-text"},
        nextBtn: {outline: "info", icon: "chevron-right"},
        prevBtn: {outline: "info", icon: "chevron-left"},
        submitBtn: {color: "amber", icon: "check", text: "Remove Checklist"},
        lineColor: "amber"
      },
      checklist: {
        avatarSet: false, bannerSet: false, accessToken: "", hasDocs: false,
        customizedHome: false, hasCategories: false
      },
      user: {
        latitude: null,
        longitude: null,
        city: null,
        country: null,
        postal: null,
        timezone: null
      },
      translateItems: [
        'page.home.welcomeMessage',
        'common.button.done'
      ]
    }
  },
  beforeMount() {
    this.user.timezone = timezone.detectTimeZone()
    if (this.user.timezone) {
      gpCookie.setCookie("timezone", this.user.timezone, 5)
    }
    if (this.user.timezone) {
      let tz = this.user.timezone
      if (tz.startsWith("America/")) this.user.country = 'usa'
      if (tz.startsWith("Europe/")) this.user.country = 'gbr'
      if (tz.startsWith("Australia/")) this.user.country = 'aus'
      if (tz.startsWith("Asia/")) this.user.country = 'hkg'
    }
  },
  mounted() {
    document.title = "Complete Registration"
    api.get("/public/countryList").then(
      (results) => {
        this.countries = results.data
        for (const country of this.countries) {
          if (country.alpha3Code != "GBL") this.searchCountries.push({
                                                                       text: country.name,
                                                                       value: country.alpha3Code.toLowerCase()
                                                                     })
        }
        for (let i = 0; i < iso.all().length; i++) {
          let row = iso.all()[i]
          let c = {text: row.country, value: row.alpha3.toLowerCase()}
          this.searchCountries.push(c)
        }
      },
      (error) => {
        this.$notify.error({message: "Error fetching countries.", timeOut: 3000})
      }).then(() => {
    })
  },
  methods: {
    uploadFile: async function (file) {
      await this.$store.dispatch("setExecuting", true)
      const reduce = require('image-blob-reduce')();
      let toUpload = file[0]
      if (file[0].size >= 2000000) {
        await reduce.toBlob(file[0], {max: 2000})
          .then(blob => {
            toUpload = blob
          })
      }
      api.postImage("/user/uploadAvatar", toUpload).then(
        (results) => {
          this.$notify.success({message: "Image uploaded successfully.", timeOut: 3000})
          this.userDetails.hasAvatar = true
          this.$store.dispatch("setAvatar", results.data.avatar)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    confirmDetected: function () {
      this.showDetectedValues = false
      this.currentStep += 2
    },
    editDetected: function () {
      this.showDetectedValues = false
      this.currentStep++
    },
    timezoneSelected: function (tz) {
      console.log("Time zone selected", tz)
      this.user.timezone = tz
      this.showTimezoneSelector = false
    },
    locationDetect: function () {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.user.latitude = position.coords.latitude
          this.user.longitude = position.coords.longitude
          this.geocode()
        }, (error) => {
          this.$notify.error({message: "Could not detect your location - " + error.message, timeOut: 4000})
          this.currentStep++
        })
      } else {
        this.$notify.error({
                             message: "Could not detect your location - your browser does not support that feature.",
                             timeOut: 4000
                           })
        this.currentStep++
      }
    },
    submit() {

    },
    locationEntry() {
      this.currentStep++
    },
    saveLocation() {
      this.updateUser(false)
    },
    updateUser(silent) {
      if (!silent) this.$store.dispatch("setExecuting", true)
      api.post("/user/setGeo", this.user).then(
        response => {
          api.getUserDetails()
        },
        error => {
          this.$notify.error({message: "Could not update location details.", timeOut: 4000})
        }).then(() => {
        if (!silent) {
          this.$store.dispatch("setExecuting", false)
          this.currentStep++
        }
      })
    },
    async geocode() {
      let url = "https://maps.googleapis.com/maps/api/geocode/json?latlng="
      url += this.user.latitude
      url += "%2C" + this.user.longitude + "&key=AIzaSyAvpwcngWd5zXy9hHgbuu_FwrNpaStWvSc"
      this.$store.dispatch("setExecuting", true)
      await axios.get(url).then(
        (response) => {
          if (response.status == 200) {
            if (response.data.results.length > 0) {
              let detailed = response.data.results[0]
              for (let d of detailed.address_components) {
                if (d.types.includes("locality")) this.user.city = d.long_name
                if (d.types.includes("postal_code")) this.user.postal = d.long_name
                if (d.types.includes("country")) {
                  for (let i = 0; i < iso.all().length; i++) {
                    let row = iso.all()[i]
                    if (row.alpha2 == d.short_name) this.user.country = row.alpha3.toLowerCase()
                  }
                }
              }
            }
            this.updateUser(true)
            this.$store.dispatch("setExecuting", false)
            this.showDetectedValues = true
          } else {
            this.$store.dispatch("setExecuting", false)
            this.$notify.error({message: "Could not fetch location details.", timeOut: 4000})
            this.currentStep++
          }
        },
        (error) => {
          this.$notify.error({message: "Could not fetch location details.", timeOut: 4000})
          this.$store.dispatch("setExecuting", false)
          this.currentStep++
        });
    }

  }
}
</script>

<style scoped>

.view-gp-back {
  height: calc(100vh);
  overflow: auto;
}

.gp-hp-gradient {
  background: -moz-linear-gradient(45deg, rgba(42, 27, 161, 0.4), rgba(29, 210, 177, 0.4) 100%);
  background: -webkit-linear-gradient(45deg, rgba(42, 27, 161, 0.4), rgba(29, 210, 177, 0.4) 100%);
  background: -o-linear-gradient(45deg, rgba(42, 27, 161, 0.4), rgba(29, 210, 177, 0.4) 100%);
  background: linear-gradient(45deg, rgba(42, 27, 161, 0.4), rgba(29, 210, 177, 0.4) 100%);
}

.opacity-8 {
  opacity: 0.8;
}

</style>
